import React, { useState, useEffect } from 'react';
import { ChevronRight, Globe, Shield, Zap } from 'lucide-react';
import { useTranslation } from "react-i18next";
import UserFooter from "../../components/common/UserFooter"
import SellerNavbar from '../../components/userNavbar/SellerNavbar';
import { useNavigate } from 'react-router-dom';
import photo from "../../Assets/Home/girl.png"
import AOS from "aos";
import "aos/dist/aos.css";

const Sellerregistration = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 800, easing: "ease-out", once: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const faqs = [
    { q: t('sellerRegistration.faq1'), a: t('sellerRegistration.faq1Answer') },
    { q: t('sellerRegistration.faq2'), a: t('sellerRegistration.faq2Answer') },
    { q: t('sellerRegistration.faq3'), a: t('sellerRegistration.faq3Answer') },
    { q: t('sellerRegistration.faq4'), a: t('sellerRegistration.faq4Answer') },
    { q: t('sellerRegistration.faq5'), a: t('sellerRegistration.faq5Answer') }
  ];

  const FAQItem = ({ faq }) => (
    <details className="group border-b border-gray-300 py-6">
      <summary className="flex justify-between items-center cursor-pointer list-none text-xl font-bold text-gray-900 hover:text-[#B08034] transition-colors">
        <span>{faq.q}</span>
        <span className="transition group-open:rotate-90 text-[#B08034]">
          <ChevronRight size={24} />
        </span>
      </summary>
      <div className="mt-4 text-gray-700 text-lg leading-relaxed pr-8">
        <p>{faq.a}</p>
      </div>
    </details>
  );

  return (
    <div className="bg-white font-sans text-gray-900 overflow-x-hidden">
      <div className='fixed w-full top-0 z-50 shadow-sm'>
        <SellerNavbar />
      </div>
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-[#1A1A1A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 items-center">
          <div className="z-10" data-aos="fade-right">
            <h1 className="text-5xl md:text-6xl font-extrabold leading-tight mb-6">
              Become a seller on <br />
              <span className="text-[#B08034]">Bharat Karigari</span>
            </h1>
            <p className="text-xl mb-8 font-light text-gray-200">
              More than half the units sold in our store are from independent sellers. Join them and grow your business.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button 
                onClick={() => navigate('/seller-login')} 
                className="bg-[#B08034] hover:bg-[#9A6F2D] text-white font-bold text-lg px-10 py-3 rounded-full transition-all"
              >
                Sign up
              </button>
            </div>
          </div>
          <div className="relative z-10" data-aos="fade-left">
            <img
              src={photo}
              alt="Seller with products"
              className="w-full rounded-2xl shadow-2xl object-cover h-[450px] border-4 border-[#333333]"
            />
          </div>
        </div>
        {/* Background decorative element */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#000000] to-transparent opacity-50"></div>
      </section>

      {/* Sub-nav banner */}
      <div className="bg-[#eaeded] py-4 border-b border-gray-300">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap gap-8 font-bold text-sm text-gray-800 uppercase tracking-wide">
          <span className="hover:text-[#B08034] cursor-pointer">Sell</span>
          <span className="hover:text-[#B08034] cursor-pointer">Pricing</span>
          <span className="hover:text-[#B08034] cursor-pointer">Grow</span>
          <span className="hover:text-[#B08034] cursor-pointer">Learn</span>
        </div>
      </div>

      {/* Why Sell With Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
              Why sell with Bharat Karigari?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Reach millions of customers, build your brand, and scale your business with our world-class infrastructure.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {[
              { Icon: Globe, title: 'Reach a Global Audience', desc: 'Connect with customers worldwide and grow your business internationally with ease.' },
              { Icon: Zap, title: 'Easy Onboarding', desc: 'Simple registration process to get your shop up and running quickly.' },
              { Icon: Shield, title: 'Secure Payments', desc: 'Reliable payment system with multiple options for your customers and fraud protection.' },
            ].map((item, index) => (
              <div key={index} className="flex flex-col items-center text-center" data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="bg-[#F9F4EB] p-5 rounded-full mb-6">
                  <item.Icon className="w-12 h-12 text-[#B08034]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600 text-lg leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 bg-[#eaeded]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16 md:flex justify-between items-end">
            <div className="max-w-2xl" data-aos="fade-right">
              <h2 className="text-4xl font-extrabold text-gray-900 mb-4">
                How to get started
              </h2>
              <p className="text-xl text-gray-600">
                Four simple steps to launch your business and start selling to millions of customers.
              </p>
            </div>
            <button 
              onClick={() => navigate('/seller-login')} 
              className="hidden md:block mt-6 md:mt-0 bg-[#B08034] hover:bg-[#9A6F2D] text-white font-bold px-8 py-3 rounded-full transition-all"
            >
              Start selling
            </button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { num: '1', title: t('sellerRegistration.step1'), desc: t('sellerRegistration.step1Desc') },
              { num: '2', title: t('sellerRegistration.step2'), desc: t('sellerRegistration.step2Desc') },
              { num: '3', title: t('sellerRegistration.step3'), desc: t('sellerRegistration.step3Desc') },
              { num: '4', title: t('sellerRegistration.step4'), desc: t('sellerRegistration.step4Desc') },
            ].map((step, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200" data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="w-14 h-14 bg-[#1A1A1A] text-[#B08034] flex items-center justify-center rounded-full text-2xl font-bold mb-6">
                  {step.num}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-6">
              {t('sellerRegistration.categoriesTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('sellerRegistration.categoriesDesc')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 justify-items-center">
            {['Textile Product', 'Metal Product', 'Woodwork', 'Ceramics', 'Stone Product', 'Leather Product'].map((category, index) => (
              <div
                key={index}
                className="text-center group cursor-pointer"
                data-aos="zoom-in"
                data-aos-delay={index * 100}
              >
                <div className="w-32 h-32 bg-[#eaeded] rounded-full flex items-center justify-center mb-4 mx-auto group-hover:bg-[#B08034] transition-colors duration-300">
                  <span className="text-[#1A1A1A] group-hover:text-white font-extrabold text-3xl transition-colors">
                    {category.charAt(0)}
                  </span>
                </div>
                <p className="font-bold text-gray-800 group-hover:text-[#B08034] transition-colors">
                  {category}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#1A1A1A] text-white text-center">
        <div className="max-w-4xl mx-auto px-6" data-aos="zoom-in">
          <h2 className="text-4xl md:text-5xl font-extrabold mb-8">Ready to start selling?</h2>
          <button 
            onClick={() => navigate('/seller-login')} 
            className="bg-[#B08034] hover:bg-[#9A6F2D] text-white font-bold text-xl px-12 py-4 rounded-full transition-all shadow-lg"
          >
            Sign up
          </button>
          {/* <p className="mt-6 text-[#9ca3af]">₹399 a month + selling fees</p> */}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-[#eaeded]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 bg-white p-8 md:p-12 rounded-3xl shadow-sm">
          <h2 className="text-4xl font-extrabold text-gray-900 mb-12" data-aos="fade-up">
            {t('sellerRegistration.faqTitle')}
          </h2>
          <div className="space-y-2" data-aos="fade-up" data-aos-delay="100">
            {faqs.map((f, i) => (
              <FAQItem faq={f} key={i} />
            ))}
          </div>
        </div>
      </section>

      <UserFooter />
    </div>
  );
};

export default Sellerregistration;